import type { FilterRule, MobileCardConfiguration } from '../../types/list';

/**
 * Shared constants and helpers for order lists
 */

/**
 * Standard required fields for all order lists
 */
export const ORDER_REQUIRED_FIELDS = [
	'_id',
	'externalId',
	'externalId2',
	'date',
	'_modified_date',
	'clientId',
	'clientName',
	'clientAddress1',
	'clientAddress2',
	'clientCity',
	'clientState',
	'clientZip',
	'status',
	'origin',
	'numberOfLines',
	'assignedRepName',
	'submitterName',
	'tax',
	'freight',
	'discount',
	'discountType',
	'subtotal',
	'total',
	'totalGP',
	'companyName',
	'alertStatus',
];

/**
 * Required fields for cart lists (no submitterName)
 */
export const CART_REQUIRED_FIELDS = [
	'_id',
	'externalId',
	'externalId2',
	'date',
	'_modified_date',
	'clientId',
	'clientName',
	'clientAddress1',
	'clientAddress2',
	'clientCity',
	'clientState',
	'clientZip',
	'status',
	'origin',
	'numberOfLines',
	'assignedRepName',
	'tax',
	'freight',
	'discount',
	'discountType',
	'subtotal',
	'total',
	'totalGP',
	'companyName',
	'alertStatus',
];

/**
 * Zones mobile card for order lists (PD-14059). Two-column body per the
 * design review: the client block (name + wrapped street / city-state-zip
 * lines) sits wide-left while Date · Rep · Lines stack right-aligned beside
 * it, so the short facts share vertical space with the client block instead
 * of adding rows. Order total is the hero; status/origin badges land in the
 * footer via their displayStyle defaults.
 *
 * Submitted By is hidden on mobile: it is usually the same person as the
 * assigned rep (reps submit their own orders), which the mobile audit read
 * as a duplicated field. Desktop keeps both columns.
 */
export const ORDER_MOBILE_CARD_CONFIG: MobileCardConfiguration = {
	cardLayout: 'zones',
	primaryFields: ['order-erp-numbers'],
	heroField: 'order-totals.total-field',
	wideFields: ['client-info'],
	hiddenFields: [
		'order-totals.subtotal-field',
		'order-costs',
		'gp-totals',
		'dates.updated-date-field',
		'reps.submitted-by-field',
	],
	// "Assigned Rep" eats half the right column before the name starts.
	labelOverrides: { 'reps.assigned-rep-field': 'Rep' },
};

/**
 * Standard sales rep filter rules (conditional based on permissions)
 */
export const SALES_REP_FILTER_RULES: Record<string, FilterRule> = {
	orderSalesRep: {
		defaultValue: [
			{
				id: 'ME',
				label: 'Me',
			},
		],
		applyDefaultIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [{ type: 'rule', action: 'orders:view:any', negated: true }],
		},
		lockedIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [{ type: 'rule', action: 'orders:view:any', negated: true }],
		},
		hiddenIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [{ type: 'rule', action: 'orders:view:any', negated: true }],
		},
	},
};
